import { useTranslation } from "react-i18next";
import { ProductGalleryThumb } from "./ProductGalleryThumb";

export interface ProductGalleryProps {
  /** Ya reunidas y sin repetir: `imageUrl` primero, luego `imageUrls`. */
  images: readonly string[];
  name: string;
  /** Cuál se está viendo en el estandarte. */
  activeIndex: number;
  onSelect: (index: number) => void;
}

/**
 * La fila de miniaturas de la ficha.
 *
 * ── Ya no lleva la imagen grande ─────────────────────────────────────
 * La grande es el estandarte (`ProductHero`), que es donde la puso la
 * ficha original: la foto a lo ancho con el degradado y el título encima.
 * Tener DOS imágenes grandes de lo mismo —el estandarte y un cuadrado
 * debajo— sería enseñar la misma foto dos veces y partir el ojo entre
 * las dos.
 *
 * ── Y por eso el índice activo sube al padre ─────────────────────────
 * Antes vivía aquí porque la imagen grande estaba aquí. Ahora la grande
 * está en el estandarte y la miniatura la cambia: si el estado se quedara
 * dentro, pulsar una miniatura no movería nada arriba. `ProductDetail` lo
 * guarda y se lo pasa a los dos.
 *
 * ── Con una sola imagen no hay fila ──────────────────────────────────
 * Una miniatura suelta es un control que no controla nada: se puede
 * pulsar y no pasa nada.
 */
export function ProductGallery({
  images,
  name,
  activeIndex,
  onSelect,
}: ProductGalleryProps) {
  const { t } = useTranslation();

  if (images.length <= 1) return null;

  return (
    <ul aria-label={t("Imágenes del producto")} className="flex flex-wrap gap-2.5">
      {images.map((image, index) => (
        <li key={`${image}-${index}`}>
          <ProductGalleryThumb
            src={image}
            name={name}
            position={index + 1}
            selected={index === activeIndex}
            onSelect={() => onSelect(index)}
          />
        </li>
      ))}
    </ul>
  );
}
